import React, { useState, useEffect } from "react";
import { Car, Building2, Sparkles } from "lucide-react";
import { ref, onValue, update, set, get } from "firebase/database";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, database } from "./firebase";
import SignIn from "./SignIn";
import SignUp from "./Signup";
import ConciergeBooking from "./ConciergeBooking";
import AdminPanel from "./AdminPanel";

export default function CarCleaningCRUD() {
  const [user, loading] = useAuthState(auth);
  const [role, setRole] = useState(null);
  const [conciergeName, setConciergeName] = useState("");
  const [authTab, setAuthTab] = useState("login");
  const [roleLoading, setRoleLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setRole(null);
      setConciergeName("");
      return;
    }

    setRoleLoading(true);
    const userRef = ref(database, `users/${user.uid}`);
    return onValue(userRef, async (snapshot) => {
      const data = snapshot.val();
      if (data && data.role) {
        setRole(data.role);
        if (data.username) setConciergeName(data.username);
        setRoleLoading(false);
        return;
      }

      // users/{uid} doesn't exist or has no role — check conciergeries for matching authUid
      try {
        const concSnap = await get(ref(database, "conciergeries"));
        const concs = concSnap.val();
        if (concs) {
          const match = Object.entries(concs).find(
            ([, v]) => v && typeof v === "object" && v.authUid === user.uid
          );
          if (match) {
            const [concId, concData] = match;
            const name = concData.name || concData.username || "";
            setRole("concierge");
            setConciergeName(name);
            // Write the users/{uid} node so future logins are instant
            await set(userRef, {
              username: name,
              email: user.email,
              role: "concierge",
              conciergerieId: concId,
            }).catch(() => {});
            setRoleLoading(false);
            return;
          }
        }
      } catch (_) {}

      setRoleLoading(false);
    });
  }, [user]);

  if (loading || (user && roleLoading)) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}
      >
        <div className="text-white text-lg font-medium">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}
      >
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          <div className="text-center pt-8 px-8">
            <div
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4"
              style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}
            >
              <Car className="text-white" size={40} />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-1">MIVIA Services</h2>
            <p className="text-gray-500 text-sm">Nettoyage automobile professionnel</p>
          </div>

          <div className="flex mt-6 border-b border-gray-200">
            <button
              onClick={() => setAuthTab("login")}
              className={`flex-1 py-3 text-sm font-semibold transition ${
                authTab === "login"
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              Connexion
            </button>
            <button
              onClick={() => setAuthTab("signup")}
              className={`flex-1 py-3 text-sm font-semibold transition ${
                authTab === "signup"
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              Inscription Conciergerie
            </button>
          </div>

          <div className="p-8">
            {authTab === "login" ? <SignIn /> : <SignUp setRole="concierge" />}
          </div>
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}
      >
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
          <div className="text-center mb-6">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3"
              style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}
            >
              <Car className="text-white" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Bienvenue !</h2>
            <p className="text-gray-500 text-sm mt-1">Sélectionnez votre profil</p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              className="w-full flex items-center gap-3 bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-semibold p-4 rounded-xl shadow hover:shadow-lg transition"
              onClick={() => {
                setRole("concierge");
                update(ref(database, `users/${user.uid}`), { role: "concierge" });
              }}
            >
              <Building2 size={20} />
              <div className="text-left">
                <p className="font-bold">Conciergerie</p>
                <p className="text-blue-200 text-xs">Réservez vos prestations</p>
              </div>
            </button>
            <button
              className="w-full flex items-center gap-3 bg-gradient-to-r from-gray-600 to-gray-800 text-white font-semibold p-4 rounded-xl shadow hover:shadow-lg transition"
              onClick={() => {
                setRole("admin");
                update(ref(database, `users/${user.uid}`), { role: "admin" });
              }}
            >
              <Sparkles size={20} />
              <div className="text-left">
                <p className="font-bold">Administration</p>
                <p className="text-gray-300 text-xs">Gérez et validez les prestations</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (role === "concierge") {
    return <ConciergeBooking user={user} conciergeName={conciergeName} />;
  }

  if (role === "admin") {
    return <AdminPanel user={user} />;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Accès refusé</h2>
        <p className="text-gray-500 mb-4">Ce profil n'a pas accès à l'application.</p>
        <button
          onClick={() => auth.signOut()}
          className="text-white font-bold py-2 px-6 rounded-lg transition"
          style={{ background: "linear-gradient(135deg, #667eea 0%, #142C93 100%)" }}
        >
          Déconnexion
        </button>
      </div>
    </div>
  );
}
